import { useEffect, useState, useCallback, useRef } from "react";
import { io } from "socket.io-client";
import logger from "../utils/logger";
import { getSocketUrl, getSocketOptions } from "../utils/socketConfig";

/**
 * Hook to connect to chat WebSocket, join/leave channels, send messages, and track online users.
 * @param {{ token: string | null }} options - accessToken; if null, socket won't connect
 */
export function useChatSocket({ token }) {
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const messageNewListenersRef = useRef(new Set());
  const messageDeletedListenersRef = useRef(new Set());
  const reactionAddedListenersRef = useRef(new Set());
  const reactionRemovedListenersRef = useRef(new Set());
  const messageEditedListenersRef = useRef(new Set());
  const messageReadListenersRef = useRef(new Set());
  const messageSeenListenersRef = useRef(new Set());
  const channelReadListenersRef = useRef(new Set());

  useEffect(() => {
    if (!token || !token.trim()) {
      return;
    }

    const socket = io(getSocketUrl(), getSocketOptions(token));
    socketRef.current = socket;

    socket.on("connect", () => {
      setIsConnected(true);
      socket.emit("presence:list");
    });

    socket.on("disconnect", () => {
      setIsConnected(false);
    });

    socket.on("presence:online", (payload) => {
      const ids = payload?.onlineIds;
      if (Array.isArray(ids)) {
        setOnlineUserIds(ids);
      }
    });

    socket.on("message:new", (message) => {
      messageNewListenersRef.current.forEach((cb) => {
        try {
          cb(message);
        } catch (e) {
          logger.error("[useChatSocket] message:new listener error:", e);
        }
      });
    });

    socket.on("message:deleted", (payload) => {
      messageDeletedListenersRef.current.forEach((cb) => {
        try {
          cb(payload);
        } catch (e) {
          logger.error("[useChatSocket] message:deleted listener error:", e);
        }
      });
    });

    socket.on("message:reaction-added", (payload) => {
      reactionAddedListenersRef.current.forEach((cb) => {
        try {
          cb(payload);
        } catch (e) {
          logger.error(
            "[useChatSocket] message:reaction-added listener error:",
            e,
          );
        }
      });
    });

    socket.on("message:reaction-removed", (payload) => {
      reactionRemovedListenersRef.current.forEach((cb) => {
        try {
          cb(payload);
        } catch (e) {
          logger.error(
            "[useChatSocket] message:reaction-removed listener error:",
            e,
          );
        }
      });
    });

    socket.on("message:edited", (payload) => {
      messageEditedListenersRef.current.forEach((cb) => {
        try {
          cb(payload);
        } catch (e) {
          logger.error("[useChatSocket] message:edited listener error:", e);
        }
      });
    });

    socket.on("message:read", (payload) => {
      messageReadListenersRef.current.forEach((cb) => {
        try {
          cb(payload);
        } catch (e) {
          logger.error("[useChatSocket] message:read listener error:", e);
        }
      });
    });

    socket.on("message:seen", (payload) => {
      messageSeenListenersRef.current.forEach((cb) => {
        try {
          cb(payload);
        } catch (e) {
          logger.error("[useChatSocket] message:seen listener error:", e);
        }
      });
    });

    socket.on("channel:read", (payload) => {
      channelReadListenersRef.current.forEach((cb) => {
        try {
          cb(payload);
        } catch (e) {
          logger.error("[useChatSocket] channel:read listener error:", e);
        }
      });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [token]);

  const joinChannel = useCallback((channelId) => {
    if (socketRef.current && channelId) {
      socketRef.current.emit("channel:join", channelId, (res) => {
        if (res?.error)
          logger.warn("[useChatSocket] channel:join error:", res.error);
      });
    }
  }, []);

  const leaveChannel = useCallback((channelId) => {
    if (socketRef.current && channelId) {
      socketRef.current.emit("channel:leave", channelId);
    }
  }, []);

  const sendMessage = useCallback(
    (
      channelId,
      content,
      attachment = null,
      attachmentUrl = null,
      replyTo = null,
    ) => {
      return new Promise((resolve) => {
        if (!socketRef.current || !channelId) {
          resolve({ error: "Invalid input" });
          return;
        }
        if (!content && !attachment && !attachmentUrl) {
          resolve({ error: "Content, attachment, or attachmentUrl required" });
          return;
        }
        const payload = { channelId };
        if (content) payload.content = content;
        if (attachment) payload.attachment = attachment;
        if (attachmentUrl) payload.attachmentUrl = attachmentUrl;
        if (replyTo) payload.replyTo = replyTo;
        socketRef.current.emit("message:send", payload, (res) => {
          resolve(res || {});
        });
      });
    },
    [],
  );

  const onMessageNew = useCallback((callback) => {
    messageNewListenersRef.current.add(callback);
    return () => {
      messageNewListenersRef.current.delete(callback);
    };
  }, []);

  const deleteMessage = useCallback((messageId, channelId) => {
    return new Promise((resolve) => {
      if (!socketRef.current || !messageId) {
        resolve({ error: "Invalid input" });
        return;
      }
      const payload = { messageId, channelId };
      socketRef.current.emit("message:delete", payload, (res) => {
        resolve(res || {});
      });
    });
  }, []);

  const onMessageDeleted = useCallback((callback) => {
    messageDeletedListenersRef.current.add(callback);
    return () => {
      messageDeletedListenersRef.current.delete(callback);
    };
  }, []);

  const addReaction = useCallback((messageId, emoji, channelId) => {
    return new Promise((resolve) => {
      if (!socketRef.current || !messageId || !emoji) {
        resolve({ error: "Invalid input" });
        return;
      }
      const payload = { messageId, emoji, channelId };
      socketRef.current.emit("message:react", payload, (res) => {
        resolve(res || {});
      });
    });
  }, []);

  const removeReaction = useCallback((messageId, emoji, channelId) => {
    return new Promise((resolve) => {
      if (!socketRef.current || !messageId || !emoji) {
        resolve({ error: "Invalid input" });
        return;
      }
      const payload = { messageId, emoji, channelId };
      socketRef.current.emit("message:unreact", payload, (res) => {
        resolve(res || {});
      });
    });
  }, []);

  const onReactionAdded = useCallback((callback) => {
    reactionAddedListenersRef.current.add(callback);
    return () => {
      reactionAddedListenersRef.current.delete(callback);
    };
  }, []);

  const onReactionRemoved = useCallback((callback) => {
    reactionRemovedListenersRef.current.add(callback);
    return () => {
      reactionRemovedListenersRef.current.delete(callback);
    };
  }, []);

  const editMessage = useCallback((messageId, content, channelId) => {
    return new Promise((resolve) => {
      if (!socketRef.current || !messageId || !content) {
        resolve({ error: "Invalid input" });
        return;
      }
      const payload = { messageId, content, channelId };
      socketRef.current.emit("message:edit", payload, (res) => {
        resolve(res || {});
      });
    });
  }, []);

  const onMessageEdited = useCallback((callback) => {
    messageEditedListenersRef.current.add(callback);
    return () => {
      messageEditedListenersRef.current.delete(callback);
    };
  }, []);

  const markMessageAsRead = useCallback((messageId, channelId) => {
    return new Promise((resolve) => {
      if (!socketRef.current || !messageId) {
        resolve({ error: "Invalid input" });
        return;
      }
      const payload = { messageId, channelId };
      socketRef.current.emit("message:read", payload, (res) => {
        resolve(res || {});
      });
    });
  }, []);

  const onMessageRead = useCallback((callback) => {
    messageReadListenersRef.current.add(callback);
    return () => {
      messageReadListenersRef.current.delete(callback);
    };
  }, []);

  const markMessageSeen = useCallback((messageId, channelId) => {
    if (!socketRef.current || !messageId || !channelId) return;
    socketRef.current.emit("message:seen", { messageId, channelId });
  }, []);

  const onMessageSeen = useCallback((callback) => {
    messageSeenListenersRef.current.add(callback);
    return () => {
      messageSeenListenersRef.current.delete(callback);
    };
  }, []);

  const markChannelAsRead = useCallback((channelId) => {
    return new Promise((resolve) => {
      if (!socketRef.current || !channelId) {
        resolve({ error: "Invalid input" });
        return;
      }
      const payload = { channelId };
      socketRef.current.emit("channel:read", payload, (res) => {
        resolve(res || {});
      });
    });
  }, []);

  const onChannelRead = useCallback((callback) => {
    channelReadListenersRef.current.add(callback);
    return () => {
      channelReadListenersRef.current.delete(callback);
    };
  }, []);

  return {
    onlineUserIds,
    isConnected,
    joinChannel,
    leaveChannel,
    sendMessage,
    deleteMessage,
    onMessageNew,
    onMessageDeleted,
    addReaction,
    removeReaction,
    onReactionAdded,
    onReactionRemoved,
    editMessage,
    onMessageEdited,
    markMessageAsRead,
    onMessageRead,
    markMessageSeen,
    onMessageSeen,
    markChannelAsRead,
    onChannelRead,
  };
}

export default useChatSocket;
